
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Edit, Info } from "lucide-react";
import { useCreateMembership, useUpdateMembership, MembershipWithServices } from "@/hooks/useMemberships";

interface Service {
  id: string;
  name: string;
  service_type: string;
}

interface AddMembershipDialogProps {
  providerId: string;
  services: Service[];
  membership?: MembershipWithServices;
  isEdit?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onMembershipAdded?: () => void;
}

interface MembershipFormData {
  name: string;
  description: string;
  membership_type: 'pack_of_sessions' | 'sessions_per_month' | 'unlimited';
  price: number;
  session_count?: number;
  service_ids: string[];
  is_subscription: boolean;
}

const AddMembershipDialog = ({ 
  providerId, 
  services, 
  membership, 
  isEdit = false,
  open,
  onOpenChange,
  onMembershipAdded 
}: AddMembershipDialogProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [selectedServices, setSelectedServices] = useState<string[]>(
    membership?.services.map(s => s.id) || []
  );

  const isOpen = open !== undefined ? open : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const createMembership = useCreateMembership();
  const updateMembership = useUpdateMembership();

  const form = useForm<MembershipFormData>({
    defaultValues: {
      name: membership?.name || "",
      description: membership?.description || "",
      membership_type: membership?.membership_type || "pack_of_sessions",
      price: membership?.price || 0,
      session_count: membership?.session_count || undefined,
      service_ids: membership?.services.map(s => s.id) || [],
      is_subscription: membership?.is_subscription || false,
    },
  });

  // Update form when membership changes
  useEffect(() => {
    if (membership) {
      form.reset({
        name: membership.name,
        description: membership.description || "",
        membership_type: membership.membership_type,
        price: membership.price,
        session_count: membership.session_count || undefined,
        service_ids: membership.services.map(s => s.id),
        is_subscription: membership.is_subscription || false,
      });
      setSelectedServices(membership.services.map(s => s.id));
    }
  }, [membership, form]);

  const watchMembershipType = form.watch("membership_type");
  const watchIsSubscription = form.watch("is_subscription");

  // Automatically set subscription status based on membership type
  useEffect(() => {
    if (watchMembershipType === 'sessions_per_month') {
      form.setValue('is_subscription', true);
    }
  }, [watchMembershipType, form]);

  const onSubmit = async (data: MembershipFormData) => {
    console.log('Submitting membership:', data);
    
    // Ensure sessions_per_month is always a subscription
    const finalData = {
      ...data,
      is_subscription: data.membership_type === 'sessions_per_month' ? true : data.is_subscription
    };
    
    const membershipData = {
      ...finalData,
      provider_id: providerId,
      service_ids: selectedServices,
      session_count: finalData.membership_type === 'unlimited' ? null : finalData.session_count,
    };

    try {
      if (membership && isEdit) {
        await updateMembership.mutateAsync({
          membershipId: membership.id,
          updates: {
            name: finalData.name,
            description: finalData.description,
            membership_type: finalData.membership_type,
            price: finalData.price,
            session_count: membershipData.session_count,
            is_subscription: finalData.is_subscription,
          },
          service_ids: selectedServices,
        });
      } else {
        await createMembership.mutateAsync(membershipData);
      }

      setOpen(false);
      form.reset();
      setSelectedServices([]);
      onMembershipAdded?.();
    } catch (error) {
      console.error('Error saving membership:', error);
    }
  };

  const handleServiceToggle = (serviceId: string) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  // Show subscription option only for unlimited memberships (sessions_per_month is always subscription)
  const showSubscriptionOption = watchMembershipType === 'unlimited';
  const isSessionsPerMonth = watchMembershipType === 'sessions_per_month';

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <Button variant="outline" size="sm">
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
        ) : (
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Add Membership
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit Membership' : 'Add New Membership'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Membership Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Premium Package" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Describe what's included in this membership..."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="membership_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Membership Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select membership type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="pack_of_sessions">Pack of Sessions</SelectItem>
                      <SelectItem value="sessions_per_month">Sessions per Month</SelectItem>
                      <SelectItem value="unlimited">Unlimited Access</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Info banner for sessions_per_month */}
            {isSessionsPerMonth && (
              <div className="flex items-start space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Automatic Subscription</p>
                  <p className="text-blue-700">
                    Sessions per month memberships are automatically set up as recurring subscriptions 
                    with monthly session credit resets.
                  </p>
                </div>
              </div>
            )}

            {/* Show subscription checkbox only for unlimited memberships */}
            {showSubscriptionOption && (
              <FormField
                control={form.control}
                name="is_subscription"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        Recurring Subscription
                      </FormLabel>
                      <p className="text-sm text-muted-foreground">
                        Enable this to make customers pay monthly for unlimited access
                      </p>
                    </div>
                  </FormItem>
                )}
              />
            )}

            {watchMembershipType !== 'unlimited' && (
              <FormField
                control={form.control}
                name="session_count"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {watchMembershipType === 'pack_of_sessions' 
                        ? 'Number of Sessions in Pack' 
                        : 'Sessions per Month'
                      }
                    </FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        min="1"
                        placeholder="Enter number of sessions"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="price"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Price (GBP) {(watchIsSubscription && showSubscriptionOption) || isSessionsPerMonth ? '- Monthly' : ''}
                  </FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      min="0" 
                      step="0.01"
                      placeholder="0.00"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-3">
              <FormLabel>Included Services</FormLabel>
              <div className="grid grid-cols-1 gap-3 max-h-40 overflow-y-auto border rounded-md p-3">
                {services.map((service) => (
                  <div key={service.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={service.id}
                      checked={selectedServices.includes(service.id)}
                      onCheckedChange={() => handleServiceToggle(service.id)}
                    />
                    <label 
                      htmlFor={service.id}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                    >
                      {service.name}
                      <Badge variant="secondary" className="ml-2 text-xs">
                        {service.service_type}
                      </Badge>
                    </label>
                  </div>
                ))}
              </div>
              {selectedServices.length === 0 && (
                <p className="text-sm text-gray-500">No services selected</p>
              )}
            </div>

            <div className="flex justify-end space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={createMembership.isPending || updateMembership.isPending}
              >
                {isEdit ? 'Update Membership' : 'Create Membership'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddMembershipDialog;
